// Typed REST wrappers for the `signals` loco service (see crates/signals).
// All requests go through the dashboard gateway: `/signals/{path}` is
// reverse-proxied to `${SIGNALS_BASE_URL}/{path}` by
// `crates/dashboard/src/controllers/signals_gateway.rs`, so the browser only
// ever talks to the dashboard origin.

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { authHeader, clearToken } from '../auth/session';

/// Prefix every signals call with the dashboard gateway mount point.
// Must stay under `/api/` — a bare `/signals` collides with the SPA's own
// client-side route of the same name, breaking browser reloads.
const SIGNALS_BASE = '/api/signals';

async function signalsFetch(path: string, init?: RequestInit): Promise<Response> {
  // The gateway is JWT-gated like the rest of /api; attach the session token.
  const res = await fetch(`${SIGNALS_BASE}${path}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), ...authHeader() },
  });
  if (res.status === 401) clearToken();
  return res;
}

/// One stored stablecoin price snapshot, as returned by
/// `GET /api/stablecoins` — mirrors the `stablecoin_price` row.
export interface StablecoinPrice {
  id: number;
  created_at: string;
  updated_at: string;
  symbol: string;
  cmc_id: number;
  name: string;
  slug: string;
  cmc_rank: number | null;
  num_market_pairs: number | null;
  is_active: boolean | null;
  is_fiat: boolean | null;
  infinite_supply: boolean | null;
  circulating_supply: number | null;
  total_supply: number | null;
  max_supply: number | null;
  minted_market_cap: number | null;
  date_added: string | null;
  coin_last_updated: string | null;
  platform_name: string | null;
  platform_token_address: string | null;
  convert: string;
  price: number | null;
  volume_24h: number | null;
  volume_change_24h: number | null;
  cex_volume_24h: number | null;
  dex_volume_24h: number | null;
  percent_change_1h: number | null;
  percent_change_24h: number | null;
  percent_change_7d: number | null;
  percent_change_30d: number | null;
  percent_change_60d: number | null;
  percent_change_90d: number | null;
  market_cap: number | null;
  market_cap_dominance: number | null;
  fully_diluted_market_cap: number | null;
  tvl: number | null;
  quote_last_updated: string | null;
}

async function fetchStablecoins(
  signal?: AbortSignal,
): Promise<StablecoinPrice[]> {
  const res = await signalsFetch('/api/stablecoins', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as StablecoinPrice[];
}

/// Latest stored price for every configured stablecoin. Refreshes every
/// 60s — the `signals` scheduler only writes a new snapshot every 5 min,
/// so a tighter cadence would just re-fetch identical data.
export function useStablecoins() {
  return useQuery<StablecoinPrice[], Error>({
    queryKey: ['signals', 'stablecoins'],
    queryFn: ({ signal }) => fetchStablecoins(signal),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/// One stored funding-rate row, as returned by `GET /api/funding-rates`.
export interface FundingRate {
  id: number;
  exchange: string;
  asset: string;
  symbol: string;
  funding_rate: number;
  funding_time: string;
  funding_interval_hours: number | null;
  mark_price: number | null;
  created_at: string;
  updated_at: string;
}

async function fetchFundingRates(
  signal?: AbortSignal,
): Promise<FundingRate[]> {
  const res = await signalsFetch('/api/funding-rates', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as FundingRate[];
}

/// Latest funding rate for every (exchange, asset) pair. Funding settles only
/// every few hours, so a 60s refresh is more than enough.
export function useFundingRates() {
  return useQuery<FundingRate[], Error>({
    queryKey: ['signals', 'funding-rates'],
    queryFn: ({ signal }) => fetchFundingRates(signal),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/// Binance BTC funding-rate history (newest first), for the trend chart.
async function fetchBinanceBtcFundingHistory(
  signal?: AbortSignal,
): Promise<FundingRate[]> {
  const res = await signalsFetch(
    '/api/funding-rates/binance/BTC/history?limit=100000',
    { signal },
  );
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as FundingRate[];
}

/// Binance BTC funding-rate history — drives the funding-rate trend chart.
/// Funding settles every 8h, so a 5-minute refresh is ample.
export function useBinanceBtcFundingHistory() {
  return useQuery<FundingRate[], Error>({
    queryKey: ['signals', 'funding-rates', 'binance', 'BTC', 'history'],
    queryFn: ({ signal }) => fetchBinanceBtcFundingHistory(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

/// One stored OHLCV candle, as returned by `GET /api/ohlcv/{asset}/{interval}`.
export interface OhlcvCandle {
  id: number;
  symbol: string;
  open_time: string;
  close_time: string;
  open_price: number;
  high_price: number;
  low_price: number;
  close_price: number;
  volume: number;
  quote_volume: number;
  trades: number;
  taker_buy_base_volume: number;
  taker_buy_quote_volume: number;
  created_at: string;
  updated_at: string;
}

async function fetchOhlcv8h(signal?: AbortSignal): Promise<OhlcvCandle[]> {
  const res = await signalsFetch('/api/ohlcv/btc/8h?limit=100000', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as OhlcvCandle[];
}

/// Binance BTC 8h OHLCV history (newest first) — drives the trend chart.
export function useOhlcv8h() {
  return useQuery<OhlcvCandle[], Error>({
    queryKey: ['signals', 'ohlcv', 'btc', '8h'],
    queryFn: ({ signal }) => fetchOhlcv8h(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

async function fetchOhlcv1d(signal?: AbortSignal): Promise<OhlcvCandle[]> {
  const res = await signalsFetch('/api/ohlcv/btc/1d?limit=100000', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as OhlcvCandle[];
}

/// Binance BTC daily OHLCV history (newest first) — the BTC line on the
/// stablecoin market-cap chart.
export function useOhlcv1d() {
  return useQuery<OhlcvCandle[], Error>({
    queryKey: ['signals', 'ohlcv', 'btc', '1d'],
    queryFn: ({ signal }) => fetchOhlcv1d(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

/// One daily stablecoin market-cap row, from `GET /api/stablecoin-mcap`
/// (DefiLlama history; the `TOTAL` symbol is the whole-market total).
export interface StablecoinMcapPoint {
  id: number;
  defillama_id: string | null;
  gecko_id: string;
  name: string;
  symbol: string;
  snapshot_date: string;
  circulating: number | null;
  circulating_usd: number | null;
  unreleased: number | null;
  minted_usd: number | null;
  bridged_usd: number | null;
  created_at: string;
  updated_at: string;
}

async function fetchStablecoinMcap(
  signal?: AbortSignal,
): Promise<StablecoinMcapPoint[]> {
  const res = await signalsFetch('/api/stablecoin-mcap', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as StablecoinMcapPoint[];
}

/// Full daily market-cap history for the top-10 stablecoins + the `TOTAL`
/// series — drives the stablecoin market-cap chart.
export function useStablecoinMcapHistory() {
  return useQuery<StablecoinMcapPoint[], Error>({
    queryKey: ['signals', 'stablecoin-mcap'],
    queryFn: ({ signal }) => fetchStablecoinMcap(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

/// One stored daily US stock price row, from
/// `GET /api/stocks/{symbol}/history` — mirrors the `stock_price_daily` row.
export interface StockBar {
  id: number;
  symbol: string;
  name: string;
  trade_date: string;
  open_price: number;
  high_price: number;
  low_price: number;
  close_price: number;
  /// Split + dividend adjusted close — the basis for drawdown.
  adj_close: number;
  volume: number;
  created_at: string;
  updated_at: string;
}

async function fetchStockHistory(
  symbol: string,
  signal?: AbortSignal,
): Promise<StockBar[]> {
  const res = await signalsFetch(
    `/api/stocks/${encodeURIComponent(symbol)}/history?limit=100000`,
    { signal },
  );
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as StockBar[];
}

/// react-query options for one stock's full daily history (newest first).
/// Shared by `useStockHistory` and the overlay chart's `useQueries`, so both
/// resolve to the same cache entry — the 美股 page fetches each stock once.
export function stockHistoryQuery(symbol: string) {
  return {
    queryKey: ['signals', 'stocks', symbol, 'history'] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchStockHistory(symbol, signal),
    enabled: !!symbol,
    // Daily data — a 10-minute refresh is ample.
    refetchInterval: 600_000,
    staleTime: 300_000,
  };
}

/// Full daily price history for one stock — drives a drawdown chart.
export function useStockHistory(symbol: string) {
  return useQuery<StockBar[], Error>(stockHistoryQuery(symbol));
}

// ---------- /api/market-overview --------------------------------------------

/// Bull / bear flags returned for each ticker. Strings match the Rust enum.
export type SmaState = 'bullish' | 'bearish' | 'neutral';
export type TickerCategory =
  | 'market'
  | 'mega_cap'
  | 'sector'
  | 'volatility'
  | 'commodity'
  | 'crypto'
  | 'industry'
  | 'theme'
  | 'macro'
  | 'other';
export type Regime = 'risk_on' | 'neutral' | 'risk_off';
export type Signal = 'bullish' | 'neutral' | 'bearish';

export interface SmaSignals {
  sma_10: SmaState;
  sma_20: SmaState;
  sma_50: SmaState;
  sma_200: SmaState;
  sma_20_above_50: SmaState;
  sma_50_above_200: SmaState;
}

export interface MarketOverviewTicker {
  ticker: string;
  name: string;
  name_cn: string;
  category: TickerCategory;
  accent: string;
  price: number;
  as_of: string;
  change_1d: number | null;
  change_1w: number | null;
  change_1m: number | null;
  change_ytd: number | null;
  change_1y: number | null;
  change_qtd: number | null;
  pct_from_52w_high: number | null;
  trend_30d: number[];
  sma: SmaSignals;
}

export interface PerformanceAvg {
  avg_1w: number | null;
  avg_1m: number | null;
  avg_ytd: number | null;
  avg_1y: number | null;
  w1_signal: Signal;
  m1_signal: Signal;
  ytd_signal: Signal;
  y1_signal: Signal;
}

export interface BreadthSnapshot {
  above_sma_20: number | null;
  above_sma_50: number | null;
  above_sma_200: number | null;
  source: string;
}

export interface Insight {
  regime: Regime;
  headline: string;
  drivers: string[];
  action_hint: string;
}

export interface DataQuality {
  source_status: 'live' | 'stale';
  stale_seconds: number | null;
  stale_reason: string | null;
}

export interface MarketOverview {
  updated_at: string | null;
  exposure_score: number | null;
  exposure_level: string;
  regime: Regime;
  performance_avg: PerformanceAvg;
  breadth: BreadthSnapshot;
  vix: number | null;
  move_index: number | null;
  skew: number | null;
  pct_from_52w_high_spy: number | null;
  insight: Insight;
  tickers: MarketOverviewTicker[];
  data_quality: DataQuality;
}

async function fetchMarketOverview(signal?: AbortSignal): Promise<MarketOverview> {
  const res = await signalsFetch('/api/market-overview', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as MarketOverview;
}

/// Single-fetch snapshot for the "市场明细" panel. The backend cron writes a
/// fresh row every 6h, so a 5-minute UI refetch is more than enough.
export function useMarketOverview() {
  return useQuery<MarketOverview, Error>({
    queryKey: ['signals', 'market-overview'],
    queryFn: ({ signal }) => fetchMarketOverview(signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

async function fetchGlobalOverview(
  signal?: AbortSignal,
): Promise<MarketOverview> {
  const res = await signalsFetch('/api/global-overview', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as MarketOverview;
}

/// "全球股市温度" panel — same shape as the US overview but the `tickers`
/// array is the 36 single-country / regional ETFs (Developed + Emerging +
/// Multi-Country). Daily data: 5-minute UI refetch is plenty.
export function useGlobalOverview() {
  return useQuery<MarketOverview, Error>({
    queryKey: ['signals', 'global-overview'],
    queryFn: ({ signal }) => fetchGlobalOverview(signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

export interface Candle {
  ts: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export type TickerDetailPeriod =
  | '1W'
  | '1M'
  | '3M'
  | '6M'
  | '1Y'
  | '5Y'
  | '10Y'
  | 'ALL';

export interface TickerDetail {
  ticker: string;
  name: string;
  name_cn: string;
  category: TickerCategory;
  accent: string;
  as_of: string | null;
  chart: Record<TickerDetailPeriod, Candle[]>;
}

async function fetchTickerDetail(
  symbol: string,
  signal?: AbortSignal,
): Promise<TickerDetail> {
  const res = await signalsFetch(
    `/api/market-overview/ticker/${encodeURIComponent(symbol)}`,
    { signal },
  );
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as TickerDetail;
}

// ---------- /api/btc-spot-prices --------------------------------------------

export interface BtcSpotTicker {
  exchange: string;
  label: string;
  quote: 'USD' | 'USDT';
  price: number | null;
  ok: boolean;
  error: string | null;
}

export interface BtcSpotStats {
  min: number | null;
  max: number | null;
  median: number | null;
  spread_pct: number | null;
  min_exchange: string | null;
  max_exchange: string | null;
}

export interface BtcSpotPrices {
  fetched_at: string;
  tickers: BtcSpotTicker[];
  stats: BtcSpotStats;
  /** Global market-average BTC price (USD) from CoinGecko. */
  global_avg: number | null;
}

async function fetchBtcSpotPrices(
  signal?: AbortSignal,
): Promise<BtcSpotPrices> {
  const res = await signalsFetch('/api/btc-spot-prices', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as BtcSpotPrices;
}

/// Cross-exchange BTC spot prices, refreshed every 15 seconds. The backend
/// fans out to 8 exchanges in parallel on each request — no DB writes.
export function useBtcSpotPrices() {
  return useQuery<BtcSpotPrices, Error>({
    queryKey: ['signals', 'btc-spot-prices'],
    queryFn: ({ signal }) => fetchBtcSpotPrices(signal),
    refetchInterval: 15_000,
    staleTime: 14_000,
  });
}

// ---------- /api/manager-13f ----------------------------------------------

export interface Manager13FSignalEntry {
  kind: 'new' | 'add' | 'reduce';
  ticker: string;
  name: string;
  name_cn: string;
  weight: number;
  weight_change: number;
}

export interface Manager13FSignals {
  top_new?: Manager13FSignalEntry;
  top_add?: Manager13FSignalEntry;
  top_reduce?: Manager13FSignalEntry;
  new_count?: number;
  exit_count?: number;
}

export interface Manager13F {
  id: number;
  slug: string;
  display_name: string;
  firm_name: string | null;
  display_name_cn: string | null;
  firm_name_cn: string | null;
  cik: string | null;
  category: string | null;
  axis: string | null;
  latest_period: string | null;
  latest_filing_date: string | null;
  reported_value: number | null;
  description_en: string | null;
  description_cn: string | null;
  tags_en: string[] | null;
  tags_cn: string[] | null;
  top_holding_ticker: string | null;
  top_holding_weight: number | null;
  quarters_tracked: number | null;
  pivot_score: number | null;
  badges_holdings_count: number | null;
  badges_top5_weight: number | null;
  badges_turnover: number | null;
  badges_option_ratio: number | null;
  signals: Manager13FSignals | null;
  raw: unknown;
  created_at: string;
  updated_at: string;
}

async function fetchManagers13F(signal?: AbortSignal): Promise<Manager13F[]> {
  const res = await signalsFetch('/api/manager-13f/managers', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as Manager13F[];
}

/// Catalogue of tracked 13F managers, refreshed by the daily backend job.
/// 5-minute UI refetch is plenty.
export function useManagers13F() {
  return useQuery<Manager13F[], Error>({
    queryKey: ['signals', 'manager-13f'],
    queryFn: ({ signal }) => fetchManagers13F(signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

// ---------- 13F per-manager detail bundle ----------------------------------

export interface Manager13FHolding {
  rank?: number;
  ticker: string;
  cusip?: string | null;
  name?: string | null;
  name_cn?: string | null;
  title_of_class?: string | null;
  put_call?: string | null;
  shares_or_principal?: number | null;
  previous_shares_or_principal?: number | null;
  shares_change?: number | null;
  reported_value?: number | null;
  weight?: number | null;
  previous_weight?: number | null;
  weight_change?: number | null;
  status?: string | null;
  is_etf?: boolean | null;
  sector?: string | null;
  trend_30d?: number[];
  return_1y?: number | null;
}

export interface Manager13FScale {
  report_period: string;
  reported_value: number;
  holdings_count: number;
  top_1_weight: number;
  top_5_weight: number;
  option_ratio: number;
  etf_ratio: number;
}

export interface Manager13FDetail {
  id: number;
  slug: string;
  report_period: string;
  filing_date: string | null;
  accession_number: string | null;
  sec_url: string | null;
  strategy_label: string | null;
  is_pivot_quarter: boolean | null;
  pivot_score: number | null;
  cumulative_growth_pct: number | null;
  scale: Manager13FScale | null;
  scale_arc: Manager13FScale[] | null;
  holdings: Manager13FHolding[] | null;
  top_holdings: Manager13FHolding[] | null;
  new_positions: Manager13FHolding[] | null;
  largest_adds: Manager13FHolding[] | null;
  largest_reduces: Manager13FHolding[] | null;
  exits: Manager13FHolding[] | null;
  option_holdings: Manager13FHolding[] | null;
  persistent_core: Manager13FHolding[] | null;
  box_structures: unknown[] | null;
  theme_exposure: Record<string, unknown> | null;
  theme_exposure_prev: Record<string, unknown> | null;
  quarterly_matrix_periods: string[] | null;
  quarterly_matrix: unknown[] | null;
  raw_stat: unknown;
  created_at: string;
  updated_at: string;
}

export interface Manager13FAbout {
  id: number;
  slug: string;
  locale: string;
  body_md: string | null;
  style_thread: string | null;
  citations: unknown[] | null;
  tags: string[] | null;
  model: string | null;
  generated_at: string | null;
  verified: boolean | null;
  raw: unknown;
  created_at: string;
  updated_at: string;
}

/// The per-quarter `stat` block carried by each Manager13F chapter — has the
/// same shape as `Manager13FDetail` minus row metadata. Used by the detail
/// panel when switching between quarters.
export interface Manager13FStat {
  manager_slug?: string;
  report_period: string;
  filing_date?: string | null;
  accession_number?: string | null;
  sec_url?: string | null;
  strategy_label?: string | null;
  is_pivot_quarter?: boolean | null;
  pivot_score?: number | null;
  scale?: Manager13FScale | null;
  scale_arc?: Manager13FScale[] | null;
  holdings?: Manager13FHolding[] | null;
  top_holdings?: Manager13FHolding[] | null;
  new_positions?: Manager13FHolding[] | null;
  largest_adds?: Manager13FHolding[] | null;
  largest_reduces?: Manager13FHolding[] | null;
  exits?: Manager13FHolding[] | null;
  option_holdings?: Manager13FHolding[] | null;
  persistent_core?: Manager13FHolding[] | null;
  box_structures?: unknown[] | null;
  theme_exposure?: Record<string, unknown> | null;
  theme_exposure_prev?: Record<string, unknown> | null;
  quarterly_matrix_periods?: string[] | null;
  quarterly_matrix?: unknown[] | null;
}

export interface Manager13FChapter {
  id: number;
  slug: string;
  report_period: string;
  idx: number;
  one_liner: string | null;
  body_md: string | null;
  stat: Manager13FStat | null;
  narrative: unknown;
  raw: unknown;
  created_at: string;
  updated_at: string;
}

export interface Manager13FBundle {
  manager: Manager13F;
  detail: Manager13FDetail | null;
  about: Manager13FAbout | null;
  chapters: Manager13FChapter[];
}

async function fetchManagerBundle(
  slug: string,
  signal?: AbortSignal,
): Promise<Manager13FBundle> {
  const res = await signalsFetch(
    `/api/manager-13f/managers/${encodeURIComponent(slug)}`,
    { signal },
  );
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as Manager13FBundle;
}

/// Aggregate detail bundle for one manager — joins the four manager_13f
/// tables. Enabled only when `slug` is provided.
export function useManagerBundle(slug: string | null) {
  return useQuery<Manager13FBundle, Error>({
    queryKey: ['signals', 'manager-13f', 'bundle', slug ?? ''],
    queryFn: ({ signal }) => fetchManagerBundle(slug as string, signal),
    enabled: !!slug,
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

// ---------- 13F cross-fund leaderboards ------------------------------------

/// One fund's quarterly position on a ticker — used by the drawer's
/// "本季对该股 增持/减持/清仓 的基金" drill-down table when a leaderboard
/// row is clicked.
export interface FundPosition {
  slug: string;
  display_name: string | null;
  display_name_cn: string | null;
  weight: number;
  weight_change: number;
  previous_weight: number;
  reported_value: number;
}

/// One ticker's quarterly aggregate across all funds that took the same
/// action (add / reduce / exit) that quarter. Three metric fields are
/// always present; the panel picks one to sort by client-side so the
/// metric toggle is a free re-sort.
export interface Manager13FLeaderboardRow {
  ticker: string;
  name: string | null;
  name_cn: string | null;
  fund_count: number;
  /// For adds: sum of `weight_change` (positive).
  /// For reduces: sum of `|weight_change|`.
  /// For exits: sum of `previous_weight` (the portfolio share exited).
  /// All values are decimal fractions (0.044 = 4.4 %).
  weight_sum: number;
  /// Sum of USD value across funds. Exits estimated as
  /// `previous_weight × fund.AUM` since the exited holding's
  /// `reported_value` is 0.
  value_sum: number;
  /// Per-fund breakdown for the drawer drill-down.
  funds: FundPosition[];
}

export interface Manager13FLeaderboards {
  period: string;
  adds: Manager13FLeaderboardRow[];
  reduces: Manager13FLeaderboardRow[];
  exits: Manager13FLeaderboardRow[];
}

interface Manager13FLeaderboardPeriods {
  periods: string[];
}

async function fetchLeaderboardPeriods(
  signal?: AbortSignal,
): Promise<string[]> {
  const res = await signalsFetch('/api/manager-13f/leaderboards/periods', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as Manager13FLeaderboardPeriods;
  return data.periods;
}

async function fetchLeaderboards(
  period: string | null,
  signal?: AbortSignal,
): Promise<Manager13FLeaderboards> {
  const url = period
    ? `/api/manager-13f/leaderboards?period=${encodeURIComponent(period)}`
    : '/api/manager-13f/leaderboards';
  const res = await signalsFetch(url, { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as Manager13FLeaderboards;
}

/// Distinct report periods available for the leaderboards, freshest first.
export function useLeaderboardPeriods() {
  return useQuery<string[], Error>({
    queryKey: ['signals', 'manager-13f', 'leaderboards', 'periods'],
    queryFn: ({ signal }) => fetchLeaderboardPeriods(signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

/// Cross-fund aggregated leaderboards for one quarter. When `period` is
/// null, the backend defaults to the most-recent quarter.
export function useLeaderboards(period: string | null) {
  return useQuery<Manager13FLeaderboards, Error>({
    queryKey: ['signals', 'manager-13f', 'leaderboards', period ?? 'latest'],
    queryFn: ({ signal }) => fetchLeaderboards(period, signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

/// Per-ticker drawer payload (1W…5Y OHLCV slices).
export function useTickerDetail(symbol: string | null) {
  return useQuery<TickerDetail, Error>({
    queryKey: ['signals', 'market-overview', 'ticker', symbol ?? ''],
    queryFn: ({ signal }) =>
      fetchTickerDetail(symbol as string, signal),
    enabled: !!symbol,
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

// ---------- /api/dollar (dollarliquidity.com) ------------------------------

export interface DollarIndicatorSummary {
  id: string;            // kebab-case path, e.g. "tga"
  label_cn: string;
  label_en: string;
  latest_date: string | null;
  latest_value: number | null;
  latest_z_score: number | null;
}

export interface DollarSeriesPoint {
  id: number;
  date: string;                          // YYYY-MM-DD
  value: number | null;
  z_score: number | null;
  created_at: string;
  updated_at: string;
}

export interface DollarSeriesPayload {
  id: string;
  label_cn: string;
  label_en: string;
  latest_date: string | null;
  points: DollarSeriesPoint[];           // oldest first
}

export interface DollarRegime {
  id: number;
  upstream_updated_at: string;
  status: string | null;
  momentum: string | null;
  confidence: string | null;
  composite_score: number | null;
  previous_score: number | null;
  percentile_5y: number | null;
  payload: unknown;                      // raw upstream JSON (drivers etc.)
  created_at: string;
  updated_at: string;
}

export interface DollarCorrelation {
  id: number;
  upstream_updated_at: string;
  payload: unknown;
  created_at: string;
  updated_at: string;
}

async function fetchDollarIndicators(signal?: AbortSignal): Promise<DollarIndicatorSummary[]> {
  const res = await signalsFetch('/api/dollar/series', { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as DollarIndicatorSummary[];
}

async function fetchDollarSeries(
  id: string,
  days: number,
  signal?: AbortSignal,
): Promise<DollarSeriesPayload> {
  const res = await signalsFetch(
    `/api/dollar/series/${encodeURIComponent(id)}?days=${days}`,
    { signal },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as DollarSeriesPayload;
}

async function fetchDollarRegime(signal?: AbortSignal): Promise<DollarRegime> {
  const res = await signalsFetch('/api/dollar/regime', { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as DollarRegime;
}

async function fetchDollarRegimeHistory(signal?: AbortSignal): Promise<DollarRegime[]> {
  const res = await signalsFetch('/api/dollar/regime/history', { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as DollarRegime[];
}

/// One row from the upstream-precomputed daily DLI history (~10y).
export interface DollarDliHistoryPoint {
  id: number;
  date: string;                           // YYYY-MM-DD
  dli_tight: number | null;                // 0..1
  dli_level: number | null;                // 0..1
  tightness_score: number | null;          // 0..100
  status: string | null;                   // tightening / neutral / loosening
  sub_score_a: number | null;
  sub_score_b: number | null;
  sub_score_c: number | null;
  sub_score_d: number | null;
  coverage_groups: number | null;
  coverage_indicators: number | null;
  a_momentum_60d: number | null;
  created_at: string;
  updated_at: string;
}

async function fetchDollarDliHistory(signal?: AbortSignal): Promise<DollarDliHistoryPoint[]> {
  const res = await signalsFetch('/api/dollar/dli/history', { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as DollarDliHistoryPoint[];
}

async function fetchDollarCorrelation(signal?: AbortSignal): Promise<DollarCorrelation> {
  const res = await signalsFetch('/api/dollar/correlation', { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as DollarCorrelation;
}

/// Catalogue of the 12 dollarliquidity indicators (id + label + latest point).
/// Used by the sidebar to surface current values; also drives the per-panel
/// title/subtitle.
export function useDollarIndicators() {
  return useQuery<DollarIndicatorSummary[], Error>({
    queryKey: ['signals', 'dollar', 'indicators'],
    queryFn: ({ signal }) => fetchDollarIndicators(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

/// History for one indicator (oldest first). `days` defaults to 1825 (~5y).
export function useDollarSeries(id: string, days: number = 1825) {
  return useQuery<DollarSeriesPayload, Error>({
    queryKey: ['signals', 'dollar', 'series', id, days],
    queryFn: ({ signal }) => fetchDollarSeries(id, days, signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
    enabled: !!id,
  });
}

/// Latest /api/regime snapshot.
export function useDollarRegime() {
  return useQuery<DollarRegime, Error>({
    queryKey: ['signals', 'dollar', 'regime'],
    queryFn: ({ signal }) => fetchDollarRegime(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

/// All stored regime snapshots (oldest first) — drives the percentile/score
/// history line chart on the regime panel.
export function useDollarRegimeHistory() {
  return useQuery<DollarRegime[], Error>({
    queryKey: ['signals', 'dollar', 'regime', 'history'],
    queryFn: ({ signal }) => fetchDollarRegimeHistory(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

/// Upstream-precomputed daily DLI tightness history (~10y). Drives the
/// main "评分历史" chart on the regime panel — full history available
/// out of the box, no need to wait for local accumulation.
export function useDollarDliHistory() {
  return useQuery<DollarDliHistoryPoint[], Error>({
    queryKey: ['signals', 'dollar', 'dli', 'history'],
    queryFn: ({ signal }) => fetchDollarDliHistory(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

/// Latest /api/correlation snapshot.
export function useDollarCorrelation() {
  return useQuery<DollarCorrelation, Error>({
    queryKey: ['signals', 'dollar', 'correlation'],
    queryFn: ({ signal }) => fetchDollarCorrelation(signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

// ---------- /api/fear-greed ----------

/// One row of the Crypto Fear & Greed Index history (alternative.me).
export interface FearGreedPoint {
  id: number;
  date: string;                         // YYYY-MM-DD (UTC)
  value: number | null;                 // 0..100
  classification: string | null;        // Extreme Fear..Extreme Greed
  timestamp_utc: string | null;         // upstream unix-seconds → UTC ISO-8601
  created_at: string;
  updated_at: string;
}

async function fetchFearGreedHistory(
  days: number | undefined,
  signal?: AbortSignal,
): Promise<FearGreedPoint[]> {
  const url = days
    ? `/api/fear-greed/history?days=${days}`
    : '/api/fear-greed/history';
  const res = await signalsFetch(url, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as FearGreedPoint[];
}

/// Crypto Fear & Greed Index history (oldest first). `days` defaults to the
/// full history (~2,500 daily rows back to 2018-02-01).
export function useFearGreedHistory(days?: number) {
  return useQuery<FearGreedPoint[], Error>({
    queryKey: ['signals', 'fear-greed', 'history', days ?? 'all'],
    queryFn: ({ signal }) => fetchFearGreedHistory(days, signal),
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

// ---------- /api/most-active (marketgrep) ----------------------------------

/// Upstream sends `null` for an SMA it cannot compute, unlike the
/// market-overview feed whose Rust enum always resolves to `neutral`. Kept as
/// a separate type so the null case has to be handled at the call site.
export type MostActiveSmaState = 'bullish' | 'bearish' | null;

export interface MostActiveRow {
  id: number;
  upstream_updated_at: string;
  snapshot_date: string;
  rank: number;
  ticker: string;
  name: string | null;
  name_cn: string | null;
  last: number | null;
  market_cap: number | null;
  volume: number | null;
  avg_volume_3m: number | null;
  value: number | null;
  // Every return is nullable: a recently listed ticker has no 1y/ytd figure.
  change_1d: number | null;
  change_1w: number | null;
  change_1m: number | null;
  change_qtd: number | null;
  change_ytd: number | null;
  change_1y: number | null;
  pct_from_52w_high: number | null;
  high_52w: number | null;
  low_52w: number | null;
  /// Variable length (6/24/30 observed) — never index blindly.
  trend_30d: number[] | null;
  /// Period key -> close series. Series lengths differ per period AND per
  /// ticker (1w=6, 1d=48, 1y=6/24/48).
  trend_by_period: Record<string, number[]> | null;
  sma: Record<string, MostActiveSmaState> | null;
}

export interface MostActiveSnapshot {
  /// Upstream's own stamp for the snapshot, null when the table is empty.
  updated_at: string | null;
  rows: MostActiveRow[];
}

async function fetchMostActive(
  signal?: AbortSignal,
): Promise<MostActiveSnapshot> {
  const res = await signalsFetch('/api/most-active/latest', { signal });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as MostActiveSnapshot;
}

/// The 50 highest-turnover US tickers. The backend refreshes every 30
/// minutes, so polling faster than that only costs requests.
export function useMostActive() {
  return useQuery<MostActiveSnapshot, Error>({
    queryKey: ['signals', 'most-active', 'latest'],
    queryFn: ({ signal }) => fetchMostActive(signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

// ---------- /api/forex -------------------------------------------------------

/// One currency in the 外汇市场 overview — mirrors the Rust
/// `ForexOverviewRow`. Percent fields are percent-scaled (12.34 = +12.34%).
export interface ForexRow {
  code: string;
  name_cn: string;
  name_en: string;
  region: string;
  /// 1 单位该货币的美元价(最新收盘)。
  price: number;
  trade_date: string;
  change_1d: number | null;
  change_1w: number | null;
  change_1m: number | null;
  change_ytd: number | null;
  change_1y: number | null;
  /// Last ≤30 closes, chronological. Variable length — never index blindly.
  trend_30d: number[];
}

export interface ForexOverview {
  updated_at: string | null;
  rows: ForexRow[];
}

/// One stored daily FX bar (`GET /api/forex/{code}/history`), chronological.
export interface ForexBar {
  id: number;
  symbol: string;
  trade_date: string;
  open_price: number;
  high_price: number;
  low_price: number;
  close_price: number;
  /// Yahoo 不提供外汇成交量,恒为 0。
  volume: number;
  created_at: string;
  updated_at: string;
}

async function fetchForexOverview(signal?: AbortSignal): Promise<ForexOverview> {
  const res = await signalsFetch('/api/forex/latest', { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ForexOverview;
}

async function fetchForexHistory(code: string, signal?: AbortSignal): Promise<ForexBar[]> {
  const res = await signalsFetch(`/api/forex/${encodeURIComponent(code)}/history`, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ForexBar[];
}

/// 外汇市场总览。日线数据,后端 6 小时刷新——5 分钟轮询绰绰有余。
export function useForexOverview() {
  return useQuery<ForexOverview, Error>({
    queryKey: ['signals', 'forex', 'latest'],
    queryFn: ({ signal }) => fetchForexOverview(signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

/// 单币种全量日线历史 — 抽屉大图用。
export function useForexHistory(code: string | null) {
  return useQuery<ForexBar[], Error>({
    queryKey: ['signals', 'forex', code, 'history'],
    queryFn: ({ signal }) => fetchForexHistory(code ?? '', signal),
    enabled: !!code,
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

// ---------- /api/watchlist ---------------------------------------------------
// 「标的观察」 — 全球投资面板下 商品/外汇/基金/加密资产 四类的标的行情。
// 数据源同外汇:Yahoo 日线,后端 6 小时刷新,一张 `watchlist_price_daily` 表按
// `asset_class` 分区。

/// 观察标的资产类别。与后端 `/api/watchlist/{class}` 的 class 段一一对应:
/// indices→基金、commodities→商品、crypto→加密资产、forex→外汇。
export type WatchlistClass = 'indices' | 'commodities' | 'crypto' | 'forex';

/// 一个观察标的的总览行,`GET /api/watchlist/{class}/latest` 的元素。
/// 百分比字段为百分数刻度(12.34 = +12.34%),`null` 表示窗口内历史不足。
export interface WatchlistRow {
  code: string;
  name_cn: string;
  name_en: string;
  /// 细分组(如指数的「美国/欧洲/亚太」、商品的「能源/贵金属」)。
  group: string;
  /// 最新收盘价。
  price: number;
  trade_date: string;
  change_1d: number | null;
  change_1w: number | null;
  change_1m: number | null;
  change_ytd: number | null;
  change_1y: number | null;
  /// 最近 ≤30 根收盘,按时间升序。变长——不要盲目按下标取值。
  trend_30d: number[];
}

export interface WatchlistOverview {
  updated_at: string | null;
  rows: WatchlistRow[];
}

/// 一根观察标的日线(`GET /api/watchlist/{class}/{code}/history`),按时间升序。
export interface WatchlistBar {
  id: number;
  asset_class: string;
  symbol: string;
  trade_date: string;
  open_price: number;
  high_price: number;
  low_price: number;
  close_price: number;
  volume: number;
  created_at: string;
  updated_at: string;
}

async function fetchWatchlistOverview(
  assetClass: WatchlistClass,
  signal?: AbortSignal,
): Promise<WatchlistOverview> {
  const res = await signalsFetch(`/api/watchlist/${assetClass}/latest`, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as WatchlistOverview;
}

async function fetchWatchlistHistory(
  assetClass: WatchlistClass,
  code: string,
  signal?: AbortSignal,
): Promise<WatchlistBar[]> {
  const res = await signalsFetch(
    `/api/watchlist/${assetClass}/${encodeURIComponent(code)}/history`,
    { signal },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as WatchlistBar[];
}

/// 某一类观察标的的总览。日线数据,5 分钟轮询绰绰有余。
export function useWatchlistOverview(assetClass: WatchlistClass) {
  return useQuery<WatchlistOverview, Error>({
    queryKey: ['signals', 'watchlist', assetClass, 'latest'],
    queryFn: ({ signal }) => fetchWatchlistOverview(assetClass, signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

/// 单标的全量日线历史 — 抽屉大图用。
export function useWatchlistHistory(assetClass: WatchlistClass, code: string | null) {
  return useQuery<WatchlistBar[], Error>({
    queryKey: ['signals', 'watchlist', assetClass, code, 'history'],
    queryFn: ({ signal }) => fetchWatchlistHistory(assetClass, code ?? '', signal),
    enabled: !!code,
    refetchInterval: 600_000,
    staleTime: 300_000,
  });
}

// ---------- /api/power-law (BTC幂律预测) -------------------------------------
// Every number is computed by signals (crates/signals/src/controllers/power_law.rs);
// the panel only renders.

export type PowerLawCycleMode = 'up' | 'down';

export interface PowerLawOverview {
  price: number | null;
  priceDate: string | null;
  asOf: string | null;
  liveIndex: number | null;
  fear: {
    value: number | null;
    classification: string | null;
    classificationZh: string;
    date: string;
  } | null;
  cycle: {
    mode: PowerLawCycleMode;
    title: string;
    value: string;
    detail: string;
    isDual: boolean;
  };
  /// 实时幂律振荡器（算法同 BTC幂律趋势）。
  oscillator: {
    value: number;
    date: string;
    lower: number;
    upper: number;
    zone: 'oversold' | 'neutral' | 'overbought';
    zoneZh: string;
  } | null;
}

export interface PowerLawRow {
  date: string;
  low: number;
  high: number;
  close: number;
  index: number;
  bottom: number;
  peak: number;
  lnT: number;
}

export interface PowerLawSeries {
  rows: PowerLawRow[];
  fear: { date: string; value: number }[];
  yearTicks: { lnT: number; label: string }[];
  chartStart: string;
}

export interface PowerLawModelPrice {
  price: number | null;
  message: string | null;
}

export interface PowerLawRangeStats {
  days?: number;
  low?: number | null;
  high?: number | null;
  partial?: boolean;
  message: string | null;
}

export interface PowerLawCycles {
  additions: number[];
  date: string;
  surgeCrashCount: number;
  table: { days: number; count: number; pct: string }[];
  total: number;
}

export interface PowerLawCycleSettings {
  mode: PowerLawCycleMode;
  upStartDate: string;
  downStartDate: string | null;
  pumpStartDate: string | null;
}

/// GET helper for the power-law endpoints.
async function powerLawGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await signalsFetch(`/api/power-law${path}`, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

/// Write helper: surfaces the backend's validation text (loco puts it in
/// `description`) and the gateway's 403 as a readable message.
async function powerLawWrite<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await signalsFetch(`/api/power-law${path}`, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 403) throw new Error('仅管理员可以修改。');
    const err = (await res.json().catch(() => null)) as { description?: string } | null;
    throw new Error(err?.description || `保存失败（HTTP ${res.status}），请稍后重试。`);
  }
  return (await res.json()) as T;
}

/// The four live cards. The current 1d candle refreshes every 5 min
/// upstream; polling at 60 s keeps the card within one fetch of it.
export function usePowerLawOverview() {
  return useQuery<PowerLawOverview, Error>({
    queryKey: ['signals', 'power-law', 'overview'],
    queryFn: ({ signal }) => powerLawGet('/overview', signal),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/// Full daily channel + fear & greed series for the three charts.
export function usePowerLawSeries() {
  return useQuery<PowerLawSeries, Error>({
    queryKey: ['signals', 'power-law', 'series'],
    queryFn: ({ signal }) => powerLawGet('/series', signal),
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

/// Formula price for `date` + `index` (inputs already debounced by caller).
export function usePowerLawModelPrice(date: string, index: string) {
  const qs = new URLSearchParams({ date, index }).toString();
  return useQuery<PowerLawModelPrice, Error>({
    queryKey: ['signals', 'power-law', 'model-price', date, index],
    queryFn: ({ signal }) => powerLawGet(`/model-price?${qs}`, signal),
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });
}

/// Day count / low / high over a date window and index band.
export function usePowerLawRangeStats(start: string, end: string, min: string, max: string) {
  const qs = new URLSearchParams({ start, end, min, max }).toString();
  return useQuery<PowerLawRangeStats, Error>({
    queryKey: ['signals', 'power-law', 'range-stats', start, end, min, max],
    queryFn: ({ signal }) => powerLawGet(`/range-stats?${qs}`, signal),
    placeholderData: keepPreviousData,
    staleTime: 300_000,
  });
}

export function usePowerLawCycles() {
  return useQuery<PowerLawCycles, Error>({
    queryKey: ['signals', 'power-law', 'cycles'],
    queryFn: ({ signal }) => powerLawGet('/cycles', signal),
    staleTime: 300_000,
  });
}

export function usePowerLawCycleSettings() {
  return useQuery<PowerLawCycleSettings, Error>({
    queryKey: ['signals', 'power-law', 'cycle-settings'],
    queryFn: ({ signal }) => powerLawGet('/cycle-settings', signal),
    staleTime: 300_000,
  });
}

/// Admin-only: save the up/down (and pump) cycle start dates.
export function useSavePowerLawCycleSettings() {
  const queryClient = useQueryClient();
  return useMutation<
    PowerLawCycleSettings,
    Error,
    { mode: PowerLawCycleMode; startDate: string; pumpStartDate: string | null }
  >({
    mutationFn: (body) => powerLawWrite('PUT', '/cycle-settings', body),
    onSuccess: (data) => {
      queryClient.setQueryData(['signals', 'power-law', 'cycle-settings'], data);
      void queryClient.invalidateQueries({ queryKey: ['signals', 'power-law', 'overview'] });
    },
  });
}

/// Admin-only: add a cycle length and/or surge-crash occurrences, or reset.
export function useChangePowerLawCycles() {
  const queryClient = useQueryClient();
  return useMutation<
    PowerLawCycles,
    Error,
    { reset: true } | { reset?: false; days?: number; surgeCrashIncrement?: number }
  >({
    mutationFn: (arg) =>
      arg.reset
        ? powerLawWrite('DELETE', '/cycles')
        : powerLawWrite('POST', '/cycles', {
            days: arg.days,
            surgeCrashIncrement: arg.surgeCrashIncrement,
          }),
    onSuccess: (data) => {
      queryClient.setQueryData(['signals', 'power-law', 'cycles'], data);
    },
  });
}

// ---------------------------------------------------------------------------
// BTC幂律趋势 (/api/power-trend/*) — all numbers computed by signals.
// ---------------------------------------------------------------------------

export interface PowerTrendRow {
  date: string;
  days: number;
  price: number;
  trend: number;
  p2_5: number;
  p16_5: number;
  p83_5: number;
  p97_5: number;
  p90: number;
  osc: number;
  sma200w: number | null;
  /// 价格在各条带之间的刻度位置（0–100）。
  pct: number;
}

export interface PowerTrendHalving {
  number: number;
  date: string;
  blockHeight: number;
  reward: number;
  label: string;
  projected: boolean;
}

export interface PowerTrendOverview {
  rows: PowerTrendRow[];
  oscBands: { lower: number; upper: number } | null;
  halvings: PowerTrendHalving[];
  nextHalving: PowerTrendHalving | null;
  coefficients: { intercept: number; slope: number };
  asOf: string | null;
}

export interface PowerTrendEvent {
  startDate: string;
  endDate: string;
  peakDate: string;
  days: number;
  peakPrice: number;
  severity: number;
  durationDays: number;
  outlierCount: number;
  sincePrev: number | null;
}

export interface PowerTrendLogLogPoint {
  days: number;
  date: string;
  price: number | null;
  sma200w: number | null;
  pct: number | null;
  trend: number;
  p2_5: number;
  p16_5: number;
  p83_5: number;
  p97_5: number;
  regression: number | null;
}

export interface PowerTrendLogLog {
  points: PowerTrendLogLogPoint[];
  events: PowerTrendEvent[];
  regression: { a: number; k: number; formula: string } | null;
  yearTicks: { days: number; label: string }[];
  range: { minDays: number; maxDays: number } | null;
  asOf: string | null;
}

export interface PowerTrendPrediction {
  date: string;
  days: number;
  severity: number;
  price: number;
  cycleLength: number;
  severityFormula: string;
  cycleFormula: string | null;
}

export interface PowerTrendOutliers {
  rows: { date: string; price: number; trend: number; p90: number }[];
  events: PowerTrendEvent[];
  prediction: PowerTrendPrediction | null;
  asOf: string | null;
}

function usePowerTrend<T>(path: string) {
  return useQuery<T, Error>({
    queryKey: ['signals', 'power-trend', path],
    queryFn: async ({ signal }) => {
      const res = await signalsFetch(`/api/power-trend/${path}`, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as T;
    },
    refetchInterval: 300_000,
    staleTime: 150_000,
  });
}

/// 普通图 + 振荡器。各 Tab 首次激活时才挂载，因此也只在那时加载。
export const usePowerTrendOverview = () => usePowerTrend<PowerTrendOverview>('overview');
/// 双对数图（外推至 2036）。
export const usePowerTrendLogLog = () => usePowerTrend<PowerTrendLogLog>('log-log');
/// 异常值分析与下一次极端事件预测。
export const usePowerTrendOutliers = () => usePowerTrend<PowerTrendOutliers>('outliers');
